
import React from 'react';
import { saioRoutes } from './saioRoutes';
import { seoRoutes } from './seoRoutes';
import { trainingRoutes } from './trainingRoutes';
import { collaborationRoutes } from './collaborationRoutes';
import { personalCardsRoutes } from './personalCardsRoutes';

// Scale at Speed: 2Y3X Pages
const Scale2Y3XDashboard = React.lazy(() => import('@/pages/scale-2y3x/Scale2Y3XDashboard'));
const GrowthLabTeamPage = React.lazy(() => import('@/pages/scale-2y3x/GrowthLabTeamPage'));
const Scale2Y3XSprintsPage = React.lazy(() => import('@/pages/scale-2y3x/SprintsPage'));
const Scale2Y3XGoalsPage = React.lazy(() => import('@/pages/scale-2y3x/GoalsPage'));
const Scale2Y3XIdeasPage = React.lazy(() => import('@/pages/scale-2y3x/IdeasPage'));
const Scale2Y3XCurrentQuarterTasksPage = React.lazy(() => import('@/pages/scale-2y3x/CurrentQuarterTasksPage'));
const Scale2Y3XTasksPage = React.lazy(() => import('@/pages/scale-2y3x/TasksPage'));
const Scale2Y3XQuarterlyTasksPage = React.lazy(() => import('@/pages/scale-2y3x/QuarterlyTasksPage'));
const Strategy2Y3XBuilderPage = React.lazy(() => import('@/pages/scale-2y3x/Strategy2Y3XBuilderPage'));
const About2Y3XPage = React.lazy(() => import('@/pages/scale-2y3x/About2Y3XPage'));
const Scale2Y3XPhasesPage = React.lazy(() => import('@/pages/scale-2y3x/PhasesPage'));
const PerformanceInsightsPage = React.lazy(() => import('@/pages/scale-2y3x/PerformanceInsightsPage'));
const GHPerformanceInsightsPage = React.lazy(() => import('@/pages/GHPerformanceInsightsPage'));

// Business on Map Page
const BusinessOnMapPage = React.lazy(() => import('@/pages/BusinessOnMapPage'));

// Product Tours Admin Pages
const ProductToursAdmin = React.lazy(() => import('@/pages/ProductToursAdmin'));
const ProductTourBuilder = React.lazy(() => import('@/pages/ProductTourBuilder'));
const ProductTourTargetElements = React.lazy(() => import('@/pages/ProductTourTargetElements'));

// Lazy load components
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const I18nTranslationsPage = React.lazy(() => import('@/pages/I18nTranslationsPage'));
const ReportingDashboardPage = React.lazy(() => import('@/pages/ReportingDashboardPage'));
const TeamPerformancePage = React.lazy(() => import('@/pages/TeamPerformance'));
const BusinessAnnotationsPage = React.lazy(() => import('@/pages/BusinessAnnotationsPage'));
const AuthPage = React.lazy(() => import('@/pages/AuthPage'));
const PartnersPage = React.lazy(() => import('@/pages/PartnersPage'));
const BrandSetupPage = React.lazy(() => import('@/pages/BrandSetupPage'));
const BrandProductsPage = React.lazy(() => import('@/pages/BrandProductsPage'));
const ProductReportPage = React.lazy(() => import('@/pages/ProductReportPage'));
const BrandProductEnrichmentPage = React.lazy(() => import('@/pages/custom/brand-products/BrandProductEnrichmentPage'));
const CustomApiTokensPage = React.lazy(() => import('@/pages/CustomApiTokensPage'));
const ApiTokensOwnershipPage = React.lazy(() => import('@/pages/ApiTokensOwnershipPage'));
const PersonasDashboardPage = React.lazy(() => import('@/pages/PersonasDashboardPage'));
const PersonaDetailPage = React.lazy(() => import('@/pages/PersonaDetailPage'));
const PersonasPage = React.lazy(() => import('@/pages/PersonasPage'));
const PersonasSimplePage = React.lazy(() => import('@/pages/PersonasSimplePage'));
const MyPersonasPage = React.lazy(() => import('@/pages/MyPersonasPage'));
const PersonasBriefPage = React.lazy(() => import('@/pages/PersonasBriefPage'));
const PersonasBriefListPage = React.lazy(() => import('@/pages/PersonasBriefListPage'));
const PersonasBriefSettingsPage = React.lazy(() => import('@/pages/PersonasBriefSettingsPage'));
const RecruitingParticipantsPage = React.lazy(() => import('@/pages/RecruitingParticipantsPage'));
const ParticipantsPage = React.lazy(() => import('@/pages/ParticipantsPage'));
const ProjectsPage = React.lazy(() => import('@/pages/ProjectsPage'));
const PreparingInputsPage = React.lazy(() => import('@/pages/PreparingInputsPage'));
const PreparingStimuliPage = React.lazy(() => import('@/pages/PreparingStimuliPage'));
const ContactingFgPage = React.lazy(() => import('@/pages/ContactingFgPage'));
const ContactingFocusGroupPage = React.lazy(() => import('@/pages/ContactingFocusGroupPage'));
const FocusGroupsDashboardPage = React.lazy(() => import('@/pages/FocusGroupsDashboardPage'));
const FgResultsPage = React.lazy(() => import('@/pages/FgResultsPage'));
const FocusGroupResultsPage = React.lazy(() => import('@/pages/FocusGroupResultsPage'));
const FgFeedbackAnalysisPage = React.lazy(() => import('@/pages/FgFeedbackAnalysisPage'));
const BrainstormingIdeasPage = React.lazy(() => import('@/pages/BrainstormingIdeasPage'));
const IdeaDetailPage = React.lazy(() => import('@/pages/IdeaDetailPage'));
const PrioritizationPage = React.lazy(() => import('@/pages/PrioritizationPage'));
const GrowthHackingDashboardPage = React.lazy(() => import('@/pages/GrowthHackingDashboardPage'));
const AboutGrowthHackingPage = React.lazy(() => import('@/pages/AboutGrowthHackingPage'));
const GrowthPlanningPage = React.lazy(() => import('@/pages/GrowthPlanningPage'));
const GrowthPlanPage = React.lazy(() => import('@/pages/GrowthPlanPage'));
const GrowthMetricsCockpitPage = React.lazy(() => import('@/pages/GrowthMetricsCockpitPage'));
const GrowthTaskingPage = React.lazy(() => import('@/pages/GrowthTaskingPage'));
const TasksPage = React.lazy(() => import('@/pages/TasksPage'));
const TaskDetailPage = React.lazy(() => import('@/pages/TaskDetailPage'));
const FriendlyTaskDetailPage = React.lazy(() => import('@/pages/FriendlyTaskDetailPage'));
const MyTasksPage = React.lazy(() => import('@/pages/MyTasksPage'));
const MyTasksAllBrandsPage = React.lazy(() => import('@/pages/MyTasksAllBrandsPage'));
const TasksMonitoringAllBrandsPage = React.lazy(() => import('@/pages/TasksMonitoringAllBrandsPage'));
const TasksMonitoringPage = React.lazy(() => import('@/pages/TasksMonitoringPage'));
const TasksAgingPage = React.lazy(() => import('@/pages/TasksAgingPage'));
const GrowthTasksPage = React.lazy(() => import('@/pages/GrowthTasksPage'));
const GrowthLearningsPage = React.lazy(() => import('@/pages/GrowthLearningsPage'));
const GrowthSquadPage = React.lazy(() => import('@/pages/GrowthSquadPage'));
const GrowthOnePagerPage = React.lazy(() => import('@/pages/GrowthOnePagerPage'));
const GrowthPillarsPage = React.lazy(() => import('@/pages/GrowthPillarsPage'));
const GrowthPillarsOverviewPage = React.lazy(() => import('@/pages/GrowthPillarsOverviewPage'));
const GrowthMetricsPage = React.lazy(() => import('@/pages/GrowthMetricsPage'));
const GrowthMetricsOverviewPage = React.lazy(() => import('@/pages/GrowthMetricsOverviewPage'));
const SalesChannelsPage = React.lazy(() => import('@/pages/SalesChannelsPage'));
const SprintsPage = React.lazy(() => import('@/pages/SprintsPage'));
const TaskPlanningPage = React.lazy(() => import('@/pages/TaskPlanningPage'));
const PlanningSheetPage = React.lazy(() => import('@/pages/PlanningSheetPage'));
const ProductWeightsPage = React.lazy(() => import('@/pages/ProductWeightsPage'));
const SeoDashboardPage = React.lazy(() => import('@/pages/SeoDashboardPage'));
const KeywordResearchPage = React.lazy(() => import('@/pages/KeywordResearchPage'));
const KeywordHoppingPage = React.lazy(() => import('@/pages/KeywordHoppingPage'));
const BrandedKeywordsPage = React.lazy(() => import('@/pages/BrandedKeywordsPage'));
const SearchKeywordsPage = React.lazy(() => import('@/pages/SearchKeywordsPage'));
const SerpsResearchPage = React.lazy(() => import('@/pages/SerpsResearchPage'));
const AiOverviewResearchPage = React.lazy(() => import('@/pages/AiOverviewResearchPage'));
const Keywords5wsPage = React.lazy(() => import('@/pages/Keywords5wsPage'));
const KeywordAssistantPage = React.lazy(() => import('@/pages/KeywordAssistantPage'));
const OnPageSeoAuditPage = React.lazy(() => import('@/pages/OnPageSeoAuditPage'));
const ThreeDAnalysisPage = React.lazy(() => import('@/pages/ThreeDAnalysisPage'));
const ThreeDAnalysisStep2Page = React.lazy(() => import('@/pages/ThreeDAnalysisStep2Page'));
const ThreeDAnalysisStep3Page = React.lazy(() => import('@/pages/ThreeDAnalysisStep3Page'));
const ThreeDAnalysisStep4Page = React.lazy(() => import('@/pages/ThreeDAnalysisStep4Page'));
const HistoricalDeclinesPage = React.lazy(() => import('@/pages/HistoricalDeclinesPage'));
const SeoSelectionGroupsPage = React.lazy(() => import('@/pages/SeoSelectionGroupsPage'));
const PpcSelectionGroupsPage = React.lazy(() => import('@/pages/PpcSelectionGroupsPage'));
const PredictiveAnalyticsPage = React.lazy(() => import('@/pages/PredictiveAnalyticsPage'));
const RankingsPage = React.lazy(() => import('@/pages/RankingsPage'));
const RunAnalysisPage = React.lazy(() => import('@/pages/RunAnalysisPage'));
const SchedulerPage = React.lazy(() => import('@/pages/SchedulerPage'));
const ContentBriefsPage = React.lazy(() => import('@/pages/ContentBriefsPage'));
const UploadContentIdeasPage = React.lazy(() => import('@/pages/UploadContentIdeasPage'));
const ContentIdeasPage = React.lazy(() => import('@/pages/ContentIdeasPage'));
const GettingTopArticlesPage = React.lazy(() => import('@/pages/GettingTopArticlesPage'));
const GettingProperStructurePage = React.lazy(() => import('@/pages/GettingProperStructurePage'));
const ContentCalendarPage = React.lazy(() => import('@/pages/ContentCalendarPage'));
const LinkedinContentIdeasPage = React.lazy(() => import('@/pages/LinkedinContentIdeasPage'));
const LinkedinPostsSuggestedPage = React.lazy(() => import('@/pages/LinkedinPostsSuggestedPage'));
const CmPostsSuggestedPage = React.lazy(() => import('@/pages/CmPostsSuggestedPage'));
const CmQualityCheckerOutgoingPage = React.lazy(() => import('@/pages/CmQualityCheckerOutgoingPage'));
const CmQualityCheckerIncomingPage = React.lazy(() => import('@/pages/CmQualityCheckerIncomingPage'));
const CmPostsSelectedPage = React.lazy(() => import('@/pages/CmPostsSelectedPage'));
const SmPostsSelectedPage = React.lazy(() => import('@/pages/SmPostsSelectedPage'));
const SocialMediaChannelSuggestionPage = React.lazy(() => import('@/pages/SocialMediaChannelSuggestionPage'));
const SmInstagramAnalysisPage = React.lazy(() => import('@/pages/SmInstagramAnalysisPage'));
const SmInstagramAnalysisTempPage = React.lazy(() => import('@/pages/SmInstagramAnalysisTempPage'));
const SmInstagramProfileComparisonsPage = React.lazy(() => import('@/pages/SmInstagramProfileComparisonsPage'));
const SMCompetitorsListPage = React.lazy(() => import('@/pages/SMCompetitorsListPage'));
const CompetitorsKeywordsPage = React.lazy(() => import('@/pages/CompetitorsKeywordsPage'));
const CompetitorsContentPage = React.lazy(() => import('@/pages/CompetitorsContentPage'));
const CompetitorsBacklinksPage = React.lazy(() => import('@/pages/CompetitorsBacklinksPage'));
const SmSizesPage = React.lazy(() => import('@/pages/SmSizesPage'));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const SalesSettingsPage = React.lazy(() => import('@/pages/SalesSettingsPage'));
const BusinessTerminologyPage = React.lazy(() => import('@/pages/BusinessTerminologyPage'));
const UserSettingsPage = React.lazy(() => import('@/pages/UserSettingsPage'));
const BrandSettingsPage = React.lazy(() => import('@/pages/BrandSettingsPage'));
const ClonePromptsPage = React.lazy(() => import('@/pages/ClonePromptsPage'));
const CloneGrowthOsCoursesPage = React.lazy(() => import('@/pages/CloneGrowthOsCoursesPage'));
const PlansAndBillingPage = React.lazy(() => import('@/pages/PlansAndBillingPage'));
const PlansAndBillingUsagePage = React.lazy(() => import('@/pages/PlansAndBillingUsagePage'));
const SeoSettingsPage = React.lazy(() => import('@/pages/SeoSettingsPage'));
const SocialMediaSettingsPage = React.lazy(() => import('@/pages/SocialMediaSettingsPage'));
const ContentMarketingSettingsPage = React.lazy(() => import('@/pages/ContentMarketingSettingsPage'));
const ContentMarketingApiTokensPage = React.lazy(() => import('@/pages/ContentMarketingApiTokensPage'));
const SaioApiTokensPage = React.lazy(() => import('@/pages/SaioApiTokensPage'));
const SeoApiTokensPage = React.lazy(() => import('@/pages/SeoApiTokensPage'));
const SeoCrawlSettingsPage = React.lazy(() => import('@/pages/SeoCrawlSettingsPage'));
const SeoAnalyticsPage = React.lazy(() => import('@/pages/SeoAnalyticsPage'));
const AnalyticsPage = React.lazy(() => import('@/pages/AnalyticsPage'));
const AnnotationsPage = React.lazy(() => import('@/pages/AnnotationsPage'));
const AnnotationReportsPage = React.lazy(() => import('@/pages/AnnotationReportsPage'));
const AnnotationReportDetailPage = React.lazy(() => import('@/pages/AnnotationReportDetailPage'));
const AnnotationPromptsPage = React.lazy(() => import('@/pages/AnnotationPromptsPage'));
const AnnotationSettingsPage = React.lazy(() => import('@/pages/AnnotationSettingsPage'));
const IntegrationsPage = React.lazy(() => import('@/pages/IntegrationsPage'));
const PromptsPage = React.lazy(() => import('@/pages/PromptsPage'));
const ApiCallsAuditPage = React.lazy(() => import('@/pages/ApiCallsAuditPage'));
const ActivityLogPage = React.lazy(() => import('@/pages/ActivityLogPage'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const BrandTeamPage = React.lazy(() => import('@/pages/BrandTeamPage'));
const TeamMemberProfilePage = React.lazy(() => import('@/pages/TeamMemberProfilePage'));
const RoleManagementPage = React.lazy(() => import('@/pages/RoleManagementPage'));
const NavigationSettingsPage = React.lazy(() => import('@/pages/NavigationSettingsPage'));
const BrandMarktechPage = React.lazy(() => import('@/pages/BrandMarktechPage'));
const VectorStorePage = React.lazy(() => import('@/pages/VectorStorePage'));
const ManageTagsPage = React.lazy(() => import('@/pages/settings/ManageTagsPage'));
const TaskCreationSettingsPage = React.lazy(() => import('@/pages/TaskCreationSettingsPage'));
const TechnicalReadinessPage = React.lazy(() => import('@/pages/settings/TechnicalReadinessPage'));
const TaskNotificationsSettingsPage = React.lazy(() => import('@/pages/TaskNotificationsSettingsPage'));
const TrainingLinkedinPage = React.lazy(() => import('@/pages/TrainingLinkedinPage'));
const BrandOnboardingPage = React.lazy(() => import('@/pages/BrandOnboardingPage'));
const PersonaDetailsPage = React.lazy(() => import('@/pages/PersonaDetailsPage'));
const SaioDashboardPage = React.lazy(() => import('@/pages/SaioDashboardPage'));
const SeoDiagnosticPage = React.lazy(() => import('@/pages/SeoDiagnosticPage'));
const SeoTrendsAnalysisPage = React.lazy(() => import('@/pages/SeoTrendsAnalysisPage'));
const SeoTestingPage = React.lazy(() => import('@/pages/SeoTestingPage'));
const YouTubeSEOPage = React.lazy(() => import('@/pages/YouTubeSEOPage'));
const YouTubeSEOKeywordsPage = React.lazy(() => import('@/pages/YouTubeSEOKeywordsPage'));
const YouTubeSEOSuggestionsPage = React.lazy(() => import('@/pages/YouTubeSEOSuggestionsPage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));
const SopDashboardPage = React.lazy(() => import('@/pages/SopDashboardPage'));
const SopsPage = React.lazy(() => import('@/pages/SopsPage'));
const BusinessMetricsPage = React.lazy(() => import('@/pages/BusinessMetricsPage'));
const BusinessMetricsSettingsPage = React.lazy(() => import('@/pages/BusinessMetricsSettingsPage'));
const BusinessEventWebhookPage = React.lazy(() => import('@/pages/BusinessEventWebhookPage'));
const IncomingEventLogPage = React.lazy(() => import('@/pages/IncomingEventLogPage'));
const ReportingDashboardConfigPage = React.lazy(() => import('@/pages/ReportingDashboardConfigPage'));
const ProjectMomsPage = React.lazy(() => import('@/pages/ProjectMomsPage'));
const StartHerePage = React.lazy(() => import('@/pages/StartHerePage'));
const TemplateIndustriesPage = React.lazy(() => import('@/pages/TemplateIndustriesPage'));
const TemplateUsecasesPage = React.lazy(() => import('@/pages/TemplateUsecasesPage'));
const TemplateDetailPage = React.lazy(() => import('@/pages/TemplateDetailPage'));
const TemplateTaskAssignmentPage = React.lazy(() => import('@/pages/TemplateTaskAssignmentPage'));
const ProjectMomsTaskAssignmentPage = React.lazy(() => import('@/pages/ProjectMomsTaskAssignmentPage'));
const TaskSchedulingPage = React.lazy(() => import('@/pages/TaskSchedulingPage'));
const TaskNotificationsPage = React.lazy(() => import('@/pages/TaskNotificationsPage'));
const DynamicCompetitorClassificationPage = React.lazy(() => import('@/pages/DynamicCompetitorClassificationPage'));
const DynamicPeopleClassificationPage = React.lazy(() => import('@/pages/DynamicPeopleClassificationPage'));
const ScheduledTasksPage = React.lazy(() => import('@/pages/ScheduledTasksPage'));
const MyOverallDashboardPage = React.lazy(() => import('@/pages/MyOverallDashboardPage'));
const MyOverallActivityPage = React.lazy(() => import('@/pages/MyOverallActivity'));
const RetailDataPage = React.lazy(() => import('@/pages/RetailDataPage'));
const MicroPerformanceAnalysisPage = React.lazy(() => import('@/pages/MicroPerformanceAnalysisPage'));
const RetailStorePerformancePage = React.lazy(() => import('@/pages/RetailStorePerformancePage'));
const RetailStoreDataLoadingPage = React.lazy(() => import('@/pages/RetailStoreDataLoadingPage'));
const PerformanceMarketingDashboardPage = React.lazy(() => import('@/pages/PerformanceMarketingDashboardPage'));
const LinkedInAdsAnalysisPage = React.lazy(() => import('@/pages/LinkedInAdsAnalysisPage'));
const GoogleAdsAnalysisPage = React.lazy(() => import('@/pages/GoogleAdsAnalysisPage'));
const GoogleAdsAIInsightsPage = React.lazy(() => import('@/pages/GoogleAdsAIInsightsPage'));
const GoogleAdsAIAnalysisPage = React.lazy(() => import('@/pages/GoogleAdsAIAnalysisPage'));
const GoogleAdsProcessingPage = React.lazy(() => import('@/pages/GoogleAdsProcessingPage'));
const GoogleAdsSpendByProductPage = React.lazy(() => import('@/pages/GoogleAdsSpendByProductPage'));
const PMPromptsPage = React.lazy(() => import('@/pages/PMPromptsPage'));
const PerformanceMarketingSettingsPage = React.lazy(() => import('@/pages/PerformanceMarketingSettingsPage'));
const FacebookAdsAnalysisPage = React.lazy(() => import('@/pages/FacebookAdsAnalysisPage'));
const AnalyticsEnginePage = React.lazy(() => import('@/pages/AnalyticsEnginePage'));
const JobReportingPage = React.lazy(() => import('@/pages/JobReportingPage'));
const UtmBuilderPage = React.lazy(() => import('@/pages/UtmBuilderPage'));
const EmailMarketingReportPage = React.lazy(() => import('@/pages/EmailMarketingReportPage'));
const HubSpotTestPage = React.lazy(() => import('@/pages/HubSpotTestPage'));
const VisualGenerationPage = React.lazy(() => import('@/pages/VisualGenerationPage'));
const MetaGeneratorPage = React.lazy(() => import('@/pages/MetaGeneratorPage'));
const EcommerceSeoPage = React.lazy(() => import('@/pages/EcommerceSeoPage'));
const KeydatesPage = React.lazy(() => import('@/pages/KeydatesPage'));
const KeydatesPromptsPage = React.lazy(() => import('@/pages/KeydatesPromptsPage'));
const FteConfigPage = React.lazy(() => import('@/pages/FteConfigPage'));
const HubSpotApiTokensPage = React.lazy(() => import('@/pages/HubSpotApiTokensPage'));
const GhlApiTokensPage = React.lazy(() => import('@/pages/GhlApiTokensPage'));
const SlackApiTokensPage = React.lazy(() => import('@/pages/SlackApiTokensPage'));
const GoogleSheetsApiTokensPage = React.lazy(() => import('@/pages/GoogleSheetsApiTokensPage'));

// Quiz Pages
const QuizListPage = React.lazy(() => import('@/pages/QuizListPage'));
const QuizBuilderPage = React.lazy(() => import('@/pages/QuizBuilderPage'));
const QuizPreviewPage = React.lazy(() => import('@/pages/QuizPreviewPage'));
const QuizTemplatesPage = React.lazy(() => import('@/pages/QuizTemplatesPage'));
const QuizStylesPage = React.lazy(() => import('@/pages/QuizStylesPage'));
const QuizPromptsPage = React.lazy(() => import('@/pages/QuizPromptsPage'));
const QuizSourcesPage = React.lazy(() => import('@/pages/QuizSourcesPage'));
const QuizPerformancePage = React.lazy(() => import('@/pages/QuizPerformancePage'));

// Press Releases Page
const PressReleasesPage = React.lazy(() => import('@/pages/PressReleasesPage'));

// Journalists Page
const JournalistsPage = React.lazy(() => import('@/pages/Journalists'));

// Support Portal Pages
const SupportPortalPage = React.lazy(() => import('@/pages/SupportPortalPage'));
const SupportPortalEditorPage = React.lazy(() => import('@/pages/admin/SupportPortalEditorPage'));
const SupportPortalCategoriesPage = React.lazy(() => import('@/pages/admin/SupportPortalCategoriesPage'));

// Link Building Pages
const LinkBuildingAnalyticsPage = React.lazy(() => import('@/pages/LinkBuildingAnalyticsPage'));
const LinkBuildingPartnersPage = React.lazy(() => import('@/pages/LinkBuildingPartnersPage'));
const LinkBuildingDomainsPage = React.lazy(() => import('@/pages/LinkBuildingDomainsPage'));
const LinkBuildingExchangesPage = React.lazy(() => import('@/pages/LinkBuildingExchangesPage'));
const LinkBuildingExchangeCreatePage = React.lazy(() => import('@/pages/LinkBuildingExchangeCreatePage'));
const LinkBuildingExchangeDetailPage = React.lazy(() => import('@/pages/LinkBuildingExchangeDetailPage'));

// Poll Pages  
const PollsPage = React.lazy(() => import('@/pages/PollsPage'));
const PollPreviewPage = React.lazy(() => import('@/pages/PollPreviewPage'));
const PollBuilderPage = React.lazy(() => import('@/pages/PollBuilderPage'));
const PollResultsPage = React.lazy(() => import('@/pages/PollResultsPage'));
const PollSourcesPage = React.lazy(() => import('@/pages/PollSourcesPage'));
const PollPerformancePage = React.lazy(() => import('@/pages/PollPerformancePage'));
const PollStylesPage = React.lazy(() => import('@/pages/PollStylesPage'));

// Lead Magnets Page
const LeadMagnetsPage = React.lazy(() => import('@/pages/LeadMagnetsPage'));
const PollTemplatesPage = React.lazy(() => import('@/pages/PollTemplatesPage'));

// Mini Game Pages
const MiniGameListPage = React.lazy(() => import('@/pages/MiniGameListPage'));
const MiniGameBuilderPage = React.lazy(() => import('@/pages/MiniGameBuilderPage'));
const MiniGamePlayerPage = React.lazy(() => import('@/pages/MiniGamePlayerPage'));
const MiniGameTypesPage = React.lazy(() => import('@/pages/MiniGameTypesPage'));
const MiniGameSourcesPage = React.lazy(() => import('@/pages/MiniGameSourcesPage'));
const MiniGamePromptsPage = React.lazy(() => import('@/pages/MiniGamePromptsPage'));
const MiniGamePerformancePage = React.lazy(() => import('@/pages/MiniGamePerformancePage'));
const MiniGameTemplatesPage = React.lazy(() => import('@/pages/MiniGameTemplatesPage'));

// Documentation Hub Pages
const DocumentationHubDashboardPage = React.lazy(() => import('@/pages/DocumentationHubDashboardPage'));
const DocumentationHubEventsPage = React.lazy(() => import('@/pages/DocumentationHubEventsPage'));
const DocumentationHubDocumentsPage = React.lazy(() => import('@/pages/DocumentationHubDocumentsPage'));
const DocumentationHubTipsPage = React.lazy(() => import('@/pages/DocumentationHubTipsPage'));
const DocumentationHubSnippetsPage = React.lazy(() => import('@/pages/DocumentationHubSnippetsPage'));
const DocumentationHubTipDetailPage = React.lazy(() => import('@/pages/DocumentationHubTipDetailPage'));
const DocumentationHubSnippetDetailPage = React.lazy(() => import('@/pages/DocumentationHubSnippetDetailPage'));
const DocumentationHubFileDetailPage = React.lazy(() => import('@/pages/DocumentationHubFileDetailPage'));
const IncomingTranscriptsPage = React.lazy(() => import('@/pages/IncomingTranscriptsPage'));
const IncomingTranscriptsWebhookGuide = React.lazy(() => import('@/pages/IncomingTranscriptsWebhookGuide'));

// Newsfeed Pages
const NewsfeedDashboardPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedDashboardPage'));
const NewsfeedRssPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedRssPage'));
const NewsfeedEmbedGuide = React.lazy(() => import('@/pages/NewsfeedEmbedGuide'));
const NewsfeedSourcesPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedSourcesPage'));
const NewsfeedCollectionsPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedCollectionsPage'));
const NewsfeedFeedsPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedFeedsPage'));
const NewsfeedPromptsPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedPromptsPage'));
const NewsfeedArticleDetailPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedArticleDetailPage'));
const NewsfeedThreads = React.lazy(() => import('@/pages/newsfeed/NewsfeedThreads'));
const NewsfeedThreadEdit = React.lazy(() => import('@/pages/newsfeed/NewsfeedThreadEdit'));
const NewsfeedEmailSettingsPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedEmailSettingsPage'));
const NewsfeedLeadsPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedLeadsPage'));
const NewsfeedEmailAnalyticsPage = React.lazy(() => import('@/pages/newsfeed/NewsfeedEmailAnalyticsPage'));
const NewsfeedLeadsWebhookGuidePage = React.lazy(() => import('@/pages/newsfeed/NewsfeedLeadsWebhookGuidePage'));

// Report Shares Pages
const ReportSharesPage = React.lazy(() => import('@/pages/ReportSharesPage'));
const ReportSharesSettingsPage = React.lazy(() => import('@/pages/ReportSharesSettingsPage'));

export interface RouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  requiresAuth: boolean;
}

export const MASTER_ROUTES: RouteConfig[] = [
  // Dashboard
  { path: '/dashboard', component: Dashboard, requiresAuth: true },
  { path: '/dashboard/i18n-translations', component: I18nTranslationsPage, requiresAuth: true },
  { path: '/reporting-dashboard', component: ReportingDashboardPage, requiresAuth: true },
  { path: '/team-performance', component: TeamPerformancePage, requiresAuth: true },
  
  // Business Annotations
  { path: '/business-annotations', component: BusinessAnnotationsPage, requiresAuth: true },
  
  // Newsfeed
  { path: '/newsfeed', component: NewsfeedDashboardPage, requiresAuth: true },
  { path: '/newsfeed-rss', component: NewsfeedRssPage, requiresAuth: false },
  { path: '/newsfeed/embed-guide', component: NewsfeedEmbedGuide, requiresAuth: true },
  { path: '/newsfeed/sources', component: NewsfeedSourcesPage, requiresAuth: true },
  { path: '/newsfeed/collections', component: NewsfeedCollectionsPage, requiresAuth: true },
  { path: '/newsfeed/feeds', component: NewsfeedFeedsPage, requiresAuth: true },
  { path: '/newsfeed/prompts', component: NewsfeedPromptsPage, requiresAuth: true },
  { path: '/newsfeed/threads', component: NewsfeedThreads, requiresAuth: true },
  { path: '/newsfeed/threads/new', component: NewsfeedThreadEdit, requiresAuth: true },
  { path: '/newsfeed/threads/:id/edit', component: NewsfeedThreadEdit, requiresAuth: true },
  { path: '/newsfeed/email-settings', component: NewsfeedEmailSettingsPage, requiresAuth: true },
  { path: '/newsfeed/leads', component: NewsfeedLeadsPage, requiresAuth: true },
  { path: '/newsfeed/email-analytics', component: NewsfeedEmailAnalyticsPage, requiresAuth: true },
  { path: '/newsfeed/leads-webhook-guide', component: NewsfeedLeadsWebhookGuidePage, requiresAuth: true },
  { path: '/newsfeed/articles/:articleId', component: NewsfeedArticleDetailPage, requiresAuth: true },
  
  // Analytics Engine
  { path: '/analytics-engine', component: AnalyticsEnginePage, requiresAuth: true },
  
  // Job Reporting
  { path: '/job-reporting', component: JobReportingPage, requiresAuth: true },
  
  // Brand Setup
  { path: '/brand-setup', component: BrandSetupPage, requiresAuth: true },
  { path: '/brand-products', component: BrandProductsPage, requiresAuth: true },
  { path: '/product_report', component: ProductReportPage, requiresAuth: true },
  { path: '/brand-products/enrichment', component: BrandProductEnrichmentPage, requiresAuth: true },
  { path: '/brand-products/incoming', component: React.lazy(() => import('@/pages/BrandProductsIncoming')), requiresAuth: true },
  { path: '/engagement-points', component: React.lazy(() => import('@/pages/EngagementPointsPage')), requiresAuth: true },
  { path: '/landing-pages', component: React.lazy(() => import('@/pages/LandingPagesPage')), requiresAuth: true },
  { path: '/business-stages', component: React.lazy(() => import('@/pages/BusinessStagesPage')), requiresAuth: true },
  { path: '/custom-api-tokens', component: CustomApiTokensPage, requiresAuth: true },
  { path: '/api-tokens-ownership', component: ApiTokensOwnershipPage, requiresAuth: true },
  { path: '/brand-onboarding', component: BrandOnboardingPage, requiresAuth: true },
  
  // Partners
  { path: '/partners', component: PartnersPage, requiresAuth: true },
  { path: '/brand-onboarding-questionnaire', component: BrandOnboardingPage, requiresAuth: true },
  { path: '/brand-moodboard', component: React.lazy(() => import('@/pages/BrandMoodboardPage')), requiresAuth: true },
  { path: '/brand-assets', component: React.lazy(() => import('@/pages/BrandAssetsPage')), requiresAuth: true },
  
  // Visual Generation
  { path: '/visual-generation', component: VisualGenerationPage, requiresAuth: true },
  { path: '/visuals-creation', component: React.lazy(() => import('@/pages/VisualsCreationPage')), requiresAuth: true },
  { path: '/visual-inspiration', component: React.lazy(() => import('@/pages/VisualInspirationPage')), requiresAuth: true },
  { path: '/past-generations', component: React.lazy(() => import('@/pages/PastGenerationsPage')), requiresAuth: true },
  { path: '/creative-prompt-library', component: React.lazy(() => import('@/pages/CreativePromptLibraryPage')), requiresAuth: true },
  
  // Podcasts
  { path: '/podcast-generation', component: React.lazy(() => import('@/pages/PodcastGenerationPage')), requiresAuth: true },
  { path: '/podcast-generations', component: React.lazy(() => import('@/pages/PodcastGenerationsPage')), requiresAuth: true },
  { path: '/podcast-library', component: React.lazy(() => import('@/pages/PodcastLibraryPage')), requiresAuth: true },
  
  // Start Here & Templates
  { path: '/start-here', component: StartHerePage, requiresAuth: true },
  { path: '/template-industries', component: TemplateIndustriesPage, requiresAuth: true },
  { path: '/template-usecases', component: TemplateUsecasesPage, requiresAuth: true },
  { path: '/tmplt-industries', component: TemplateIndustriesPage, requiresAuth: true },
  { path: '/tmplt-usecases', component: TemplateUsecasesPage, requiresAuth: true },
  { path: '/template/:type/:templateId', component: TemplateDetailPage, requiresAuth: true },
  { path: '/template/:templateType/:templateId/assign-tasks', component: TemplateTaskAssignmentPage, requiresAuth: true },
  { path: '/documentation-hub/project-moms/:transcriptId/assign-tasks', component: ProjectMomsTaskAssignmentPage, requiresAuth: true },
  
  // SOPs
  { path: '/sop-dashboard', component: SopDashboardPage, requiresAuth: true },
  { path: '/project-progress-configurator', component: React.lazy(() => import('@/pages/ProjectProgressConfiguratorPage')), requiresAuth: true },
  { path: '/sops', component: SopsPage, requiresAuth: true },
  { path: '/business-metrics', component: BusinessMetricsPage, requiresAuth: true },
  { path: '/business-metrics-settings', component: BusinessMetricsSettingsPage, requiresAuth: true },
  { path: '/business-event-webhook', component: BusinessEventWebhookPage, requiresAuth: true },
  { path: '/incoming-event-log', component: IncomingEventLogPage, requiresAuth: true },
  { path: '/personal-cards', component: React.lazy(() => import('@/pages/PersonalCardsPage')), requiresAuth: true },
  
  // FTE Configuration
  { path: '/fte-config', component: FteConfigPage, requiresAuth: true },
  
  // Personas
  { path: '/personas-dashboard', component: PersonasDashboardPage, requiresAuth: true },
  { path: '/personas', component: PersonasPage, requiresAuth: true },
  { path: '/personas-simple', component: PersonasSimplePage, requiresAuth: true },
  { path: '/my-personas', component: MyPersonasPage, requiresAuth: true },
  { path: '/persona/:id', component: PersonaDetailPage, requiresAuth: true },
  { path: '/personas/:id', component: PersonaDetailPage, requiresAuth: true },
  { path: '/persona-details/:id', component: PersonaDetailsPage, requiresAuth: true },
  { path: '/personas-brief', component: PersonasBriefPage, requiresAuth: true },
  { path: '/personas-brief-list', component: PersonasBriefListPage, requiresAuth: true },
  { path: '/personas-brief-settings', component: PersonasBriefSettingsPage, requiresAuth: true },
  { path: '/performance-brief', component: React.lazy(() => import('@/pages/PerformanceBriefPage')), requiresAuth: true },
  { path: '/performance-brief/new', component: React.lazy(() => import('@/pages/PerformanceBriefNewPage')), requiresAuth: true },
  { path: '/performance-brief/edit/:id', component: React.lazy(() => import('@/pages/PerformanceBriefEditPage')), requiresAuth: true },
  { path: '/performance-brief/prompts', component: React.lazy(() => import('@/pages/PerformanceBriefPromptsPage')), requiresAuth: true },
  { path: '/performance-brief/:id', component: React.lazy(() => import('@/pages/PerformanceBriefViewPage')), requiresAuth: true },

  // Recruiting Participants
  { path: '/recruiting-participants', component: RecruitingParticipantsPage, requiresAuth: true },
  { path: '/participants', component: ParticipantsPage, requiresAuth: true },

  // Projects
  { path: '/projects', component: ProjectsPage, requiresAuth: true },
  { path: '/preparing-inputs', component: PreparingInputsPage, requiresAuth: true },
  { path: '/preparing-stimuli', component: PreparingStimuliPage, requiresAuth: true },

  // Focus Groups
  { path: '/contacting-fg', component: ContactingFgPage, requiresAuth: true },
  { path: '/contacting-focus-group', component: ContactingFocusGroupPage, requiresAuth: true },
  { path: '/focus-groups-dashboard', component: FocusGroupsDashboardPage, requiresAuth: true },
  { path: '/fg-dashboard', component: FocusGroupsDashboardPage, requiresAuth: true },
  { path: '/fg-results', component: FgResultsPage, requiresAuth: true },
  { path: '/focus-group-results', component: FocusGroupResultsPage, requiresAuth: true },
  { path: '/fg-feedback-analysis', component: FgFeedbackAnalysisPage, requiresAuth: true },

  // Brainstorming & Prioritization
  { path: '/brainstorming-ideas', component: BrainstormingIdeasPage, requiresAuth: true },
  { path: '/brainstorming-ideas/:id', component: IdeaDetailPage, requiresAuth: true },
  { path: '/prioritization', component: PrioritizationPage, requiresAuth: true },

  // Growth Hacking
  { path: '/growthhacking/about', component: AboutGrowthHackingPage, requiresAuth: true },
  { path: '/growth-hacking-dashboard', component: GrowthHackingDashboardPage, requiresAuth: true },
  { path: '/gh-dashboard', component: GrowthHackingDashboardPage, requiresAuth: true },
  { path: '/growth-metrics-cockpit', component: GrowthMetricsCockpitPage, requiresAuth: true },
  { path: '/growth-planning', component: GrowthPlanningPage, requiresAuth: true },
  { path: '/growth-plan', component: GrowthPlanPage, requiresAuth: true },
  { path: '/growth-tasking', component: GrowthTaskingPage, requiresAuth: true },
  { path: '/tasks', component: TasksPage, requiresAuth: true },
  { path: '/tasks/:brandId/:taskId', component: TaskDetailPage, requiresAuth: true },
  { path: '/task/:brandSlug/:taskSlug', component: FriendlyTaskDetailPage, requiresAuth: true },
  { path: '/tasks-journal', component: React.lazy(() => import('@/pages/TasksJournalPage')), requiresAuth: true },
  { path: '/my-tasks', component: MyTasksPage, requiresAuth: true },
  { path: '/my-tasks-all-brands', component: MyTasksAllBrandsPage, requiresAuth: true },
  { path: '/my-overall-dashboard', component: MyOverallDashboardPage, requiresAuth: true },
  { path: '/my-overall-activity', component: MyOverallActivityPage, requiresAuth: true },
  { path: '/retail-data', component: RetailDataPage, requiresAuth: true },
  { path: '/retail-store-performance', component: RetailStorePerformancePage, requiresAuth: true },
  { path: '/retail-store-data-loading', component: RetailStoreDataLoadingPage, requiresAuth: true },
  { path: '/retail-store-settings', component: React.lazy(() => import('@/pages/RetailStoreSettingsPage')), requiresAuth: true },
  { path: '/retail-store-micro-performance', component: MicroPerformanceAnalysisPage, requiresAuth: true },
  { path: '/tasks-monitoring', component: TasksMonitoringPage, requiresAuth: true },
  { path: '/tasks-monitoring-all-brands', component: TasksMonitoringAllBrandsPage, requiresAuth: true },
  { path: '/tasks-aging', component: TasksAgingPage, requiresAuth: true },
  { path: '/growth-tasks', component: GrowthTasksPage, requiresAuth: true },
  { path: '/growth-learnings', component: GrowthLearningsPage, requiresAuth: true },
  { path: '/growth-squad', component: GrowthSquadPage, requiresAuth: true },
  { path: '/growth-one-pager', component: GrowthOnePagerPage, requiresAuth: true },
  { path: '/gh-onepager', component: GrowthOnePagerPage, requiresAuth: true },
  { path: '/growth-pillars', component: GrowthPillarsPage, requiresAuth: true },
  { path: '/growth-pillars/:salesChannelId', component: GrowthPillarsPage, requiresAuth: true },
  { path: '/growth-pillars/:salesChannelId/metrics', component: GrowthMetricsOverviewPage, requiresAuth: true },
  { path: '/growth-pillars-overview', component: GrowthPillarsOverviewPage, requiresAuth: true },
  { path: '/growth-metrics', component: GrowthMetricsPage, requiresAuth: true },
  { path: '/growth-pillars/:salesChannelId/metrics/:pillarId', component: GrowthMetricsPage, requiresAuth: true },
  { path: '/growth-metrics-overview', component: GrowthMetricsOverviewPage, requiresAuth: true },
  { path: '/sales-channels', component: SalesChannelsPage, requiresAuth: true },
  { path: '/gh-performance-insights', component: GHPerformanceInsightsPage, requiresAuth: true },
  { path: '/sprints', component: SprintsPage, requiresAuth: true },
  { path: '/task-planning', component: TaskPlanningPage, requiresAuth: true },
  { path: '/planning-sheet', component: PlanningSheetPage, requiresAuth: true },
  { path: '/product-weights', component: ProductWeightsPage, requiresAuth: true },

  // SEO
  { path: '/seo-dashboard', component: SeoDashboardPage, requiresAuth: true },
  { path: '/keyword-research', component: KeywordResearchPage, requiresAuth: true },
  { path: '/keyword-hopping', component: KeywordHoppingPage, requiresAuth: true },
  { path: '/branded-keywords', component: BrandedKeywordsPage, requiresAuth: true },
  
  { path: '/serps-research', component: SerpsResearchPage, requiresAuth: true },
  { path: '/ai-overview-research', component: AiOverviewResearchPage, requiresAuth: true },
  { path: '/keywords-5ws', component: Keywords5wsPage, requiresAuth: true },
  { path: '/keyword-assistant', component: KeywordAssistantPage, requiresAuth: true },
  { path: '/on-page-seo-audit', component: OnPageSeoAuditPage, requiresAuth: true },
  { path: '/onpage-seo-audit', component: OnPageSeoAuditPage, requiresAuth: true }, // Add alias for old route
  { path: '/3d-analysis', component: ThreeDAnalysisPage, requiresAuth: true },
  { path: '/3danalysis_step1', component: ThreeDAnalysisPage, requiresAuth: true }, // Add alias for old route
  { path: '/3d-analysis-step2', component: ThreeDAnalysisStep2Page, requiresAuth: true },
  { path: '/3danalysis_step2', component: ThreeDAnalysisStep2Page, requiresAuth: true }, // Add alias for old route
  { path: '/3d-analysis-step3', component: ThreeDAnalysisStep3Page, requiresAuth: true },
  { path: '/3danalysis_step3', component: ThreeDAnalysisStep3Page, requiresAuth: true }, // Add alias for old route
  { path: '/3d-analysis-step4', component: ThreeDAnalysisStep4Page, requiresAuth: true },
  { path: '/3danalysis_step4', component: ThreeDAnalysisStep4Page, requiresAuth: true }, // Add alias for old route
  { path: '/historical-declines', component: HistoricalDeclinesPage, requiresAuth: true },
  { path: '/seo-selection-groups', component: SeoSelectionGroupsPage, requiresAuth: true },
  
  { path: '/predictive-analytics', component: PredictiveAnalyticsPage, requiresAuth: true },
  { path: '/rankings', component: RankingsPage, requiresAuth: true },
  { path: '/run-analysis', component: RunAnalysisPage, requiresAuth: true },
  { path: '/seo-diagnostic', component: SeoDiagnosticPage, requiresAuth: true },
  { path: '/seo-trends-analysis', component: SeoTrendsAnalysisPage, requiresAuth: true },
  { path: '/scheduler', component: SchedulerPage, requiresAuth: true },
  { path: '/seo-testing', component: SeoTestingPage, requiresAuth: true },
  { path: '/youtube-seo', component: YouTubeSEOPage, requiresAuth: true },
  { path: '/youtube-seo-keywords', component: YouTubeSEOKeywordsPage, requiresAuth: true },
  { path: '/youtube-seo-suggestions', component: YouTubeSEOSuggestionsPage, requiresAuth: true },

  // Content Marketing
  { path: '/seo-machine', component: React.lazy(() => import('@/pages/SeoMachinePage')), requiresAuth: true },
  { path: '/content-briefs', component: ContentBriefsPage, requiresAuth: true },
  { path: '/upload-content-ideas', component: UploadContentIdeasPage, requiresAuth: true },
  { path: '/content-ideas', component: ContentIdeasPage, requiresAuth: true },
  { path: '/getting-top-articles', component: GettingTopArticlesPage, requiresAuth: true },
  { path: '/getting-proper-structure', component: GettingProperStructurePage, requiresAuth: true },
  { path: '/content-calendar', component: ContentCalendarPage, requiresAuth: true },

  // Training Routes
  ...trainingRoutes.map(route => ({
    path: route.path,
    component: route.component,
    requiresAuth: true
  })),

  // Collaboration Routes
  ...collaborationRoutes.map(route => ({
    path: route.path,
    component: route.component,
    requiresAuth: true
  })),

  // Personal Cards Routes
  ...personalCardsRoutes.map(route => ({
    path: route.path,
    component: route.element,
    requiresAuth: route.requiresAuth ?? true
  })),

  // LinkedIn
  { path: '/short-form-content-ideas', component: LinkedinContentIdeasPage, requiresAuth: true },
  { path: '/short-form-content-pillars', component: React.lazy(() => import('@/pages/ShortFormContentPillarsPage')), requiresAuth: true },
  { path: '/short-form-posts-suggested', component: LinkedinPostsSuggestedPage, requiresAuth: true },

  // Content Management
  { path: '/cm-posts-suggested', component: CmPostsSuggestedPage, requiresAuth: true },
  { path: '/cm-posts-selected', component: CmPostsSelectedPage, requiresAuth: true },
  
  // Quality Checker
  { path: '/cm-quality-checker-outgoing', component: CmQualityCheckerOutgoingPage, requiresAuth: true },
  { path: '/cm-quality-checker-incoming', component: CmQualityCheckerIncomingPage, requiresAuth: true },

  // Social Media
  { path: '/sm-posts-selected', component: SmPostsSelectedPage, requiresAuth: true },
  { path: '/social-media-channel-suggestion', component: SocialMediaChannelSuggestionPage, requiresAuth: true },
  { path: '/sm-instagram-analysis', component: SmInstagramAnalysisPage, requiresAuth: true },
  { path: '/sm-instagram-get-feed', component: SmInstagramAnalysisTempPage, requiresAuth: true },
  { path: '/sm-instagram-profile-comparisons', component: SmInstagramProfileComparisonsPage, requiresAuth: true },
  { path: '/sm-competitors-list', component: SMCompetitorsListPage, requiresAuth: true },
  { path: '/sm-sizes', component: SmSizesPage, requiresAuth: true },

  // Dynamic Competitor Classification
  { path: '/dynamic-competitor-classification', component: DynamicCompetitorClassificationPage, requiresAuth: true },
  { path: '/dynamic-people-classification', component: DynamicPeopleClassificationPage, requiresAuth: true },
  { path: '/competitors-keywords', component: CompetitorsKeywordsPage, requiresAuth: true },
  { path: '/competitors-content', component: CompetitorsContentPage, requiresAuth: true },
  { path: '/competitors-backlinks', component: CompetitorsBacklinksPage, requiresAuth: true },

  // Performance Marketing
  { path: '/performance-marketing-dashboard', component: PerformanceMarketingDashboardPage, requiresAuth: true },
  { path: '/utm-builder', component: UtmBuilderPage, requiresAuth: true },
  { path: '/linkedin-ads-analysis', component: LinkedInAdsAnalysisPage, requiresAuth: true },
  { path: '/facebook-ads-analysis', component: FacebookAdsAnalysisPage, requiresAuth: true },
  { path: '/googleads-ads-analysis', component: GoogleAdsAnalysisPage, requiresAuth: true },
  { path: '/googleads-ads-ai-insights', component: GoogleAdsAIInsightsPage, requiresAuth: true },
  { path: '/googleads-ads-ai-insights/:brandId', component: GoogleAdsAIAnalysisPage, requiresAuth: true },
  { path: '/google-ads-processing', component: GoogleAdsProcessingPage, requiresAuth: true },
  { path: '/google-ads-spend-by-product', component: GoogleAdsSpendByProductPage, requiresAuth: true },
  { path: '/pm-prompts', component: PMPromptsPage, requiresAuth: true },
  { path: '/performance-marketing-settings', component: PerformanceMarketingSettingsPage, requiresAuth: true },

  // Settings & API Token Pages
  { path: '/settings', component: SettingsPage, requiresAuth: true },
  { path: '/sales-settings', component: SalesSettingsPage, requiresAuth: true },
  { path: '/business-terminology', component: BusinessTerminologyPage, requiresAuth: true },
  { path: '/embed-settings', component: React.lazy(() => import('@/pages/Settings/BrandEmbedSettingsPage')), requiresAuth: true },
  { path: '/user-settings', component: UserSettingsPage, requiresAuth: true },
  { path: '/push-notifications', component: BrandSettingsPage, requiresAuth: true },
  { path: '/clone-prompts', component: ClonePromptsPage, requiresAuth: true },
  { path: '/clone-growthos-courses', component: CloneGrowthOsCoursesPage, requiresAuth: true },
  { path: '/plans-and-billing', component: PlansAndBillingPage, requiresAuth: true },
  { path: '/plans-and-billing/usage', component: PlansAndBillingUsagePage, requiresAuth: true },
  { path: '/cm-api-tokens', component: ContentMarketingApiTokensPage, requiresAuth: true },
  { path: '/saio-api-tokens', component: SaioApiTokensPage, requiresAuth: true },
  { path: '/seo-api-tokens', component: SeoApiTokensPage, requiresAuth: true },
  { path: '/seo-crawl-settings', component: SeoCrawlSettingsPage, requiresAuth: true },
  { path: '/podcasts-api-tokens', component: React.lazy(() => import('@/pages/PodcastsApiTokensPage')), requiresAuth: true },
  { path: '/hubspot-api-tokens', component: HubSpotApiTokensPage, requiresAuth: true },
  { path: '/ghl-api-tokens', component: GhlApiTokensPage, requiresAuth: true },
  { path: '/slack-api-tokens', component: SlackApiTokensPage, requiresAuth: true },
  { path: '/google-sheets-api-tokens', component: GoogleSheetsApiTokensPage, requiresAuth: true },
  { path: '/cms-api-tokens', component: React.lazy(() => import('@/pages/CmsApiTokensPage')), requiresAuth: true },
  { path: '/earned-networks-api-tokens', component: React.lazy(() => import('@/pages/EarnedNetworksApiTokensPage')), requiresAuth: true },
  { path: '/reddit-subreddit-search', component: React.lazy(() => import('@/pages/RedditSubredditSearchPage')), requiresAuth: true },
  { path: '/cm-prompts', component: React.lazy(() => import('@/pages/CmPromptsPage')), requiresAuth: true },
  { path: '/podcasts-prompts', component: React.lazy(() => import('@/pages/PodcastsPromptsPage')), requiresAuth: true },
  { path: '/press-releases-prompts', component: React.lazy(() => import('@/pages/PressReleasesPromptsPage')), requiresAuth: true },
  { path: '/featureimage-prompts', component: React.lazy(() => import('@/pages/FeatureImagePromptsPage')), requiresAuth: true },
  { path: '/content-pillar-prompts', component: React.lazy(() => import('@/pages/ContentPillarPromptsPage')), requiresAuth: true },
  { path: '/seo-settings', component: SeoSettingsPage, requiresAuth: true },
  { path: '/settings-seo', component: SeoSettingsPage, requiresAuth: true }, // Add alias for old route
  { path: '/social-media-settings', component: SocialMediaSettingsPage, requiresAuth: true },
  { path: '/content-marketing-settings', component: ContentMarketingSettingsPage, requiresAuth: true },
  { path: '/cm-settings', component: ContentMarketingSettingsPage, requiresAuth: true },
  { path: '/seo-analytics', component: SeoAnalyticsPage, requiresAuth: true },
  { path: '/analytics', component: AnalyticsPage, requiresAuth: true },
  { path: '/annotations', component: AnnotationsPage, requiresAuth: true },
  { path: '/annotations/reports', component: AnnotationReportsPage, requiresAuth: true },
  { path: '/annotations/reports/:id', component: AnnotationReportDetailPage, requiresAuth: true },
  { path: '/annotations/prompts', component: AnnotationPromptsPage, requiresAuth: true },
  { path: '/annotations/settings', component: AnnotationSettingsPage, requiresAuth: true },
  { path: '/integrations', component: IntegrationsPage, requiresAuth: true },
  { path: '/prompts', component: PromptsPage, requiresAuth: true },
  { path: '/api-calls-audit', component: ApiCallsAuditPage, requiresAuth: true },
  { path: '/activity-log', component: ActivityLogPage, requiresAuth: true },
  { path: '/activity', component: ActivityLogPage, requiresAuth: true },
  { path: '/notifications', component: NotificationsPage, requiresAuth: true },
  { path: '/brand-team', component: BrandTeamPage, requiresAuth: true },
  { path: '/team/member/:memberId', component: TeamMemberProfilePage, requiresAuth: true },
  { path: '/role-management', component: RoleManagementPage, requiresAuth: true },
  { path: '/navigation-settings', component: NavigationSettingsPage, requiresAuth: true },
  { path: '/brand-marktech', component: BrandMarktechPage, requiresAuth: true },
  { path: '/vector-store', component: VectorStorePage, requiresAuth: true },
  { path: '/settings/manage-tags', component: ManageTagsPage, requiresAuth: true },
  { path: '/settings/task-rules', component: TaskCreationSettingsPage, requiresAuth: true },
  { path: '/settings/technical-readiness', component: TechnicalReadinessPage, requiresAuth: true },
  { path: '/tasks-notifications-settings', component: TaskNotificationsSettingsPage, requiresAuth: true },

  // Digital Operations
  { path: '/task-scheduling', component: TaskSchedulingPage, requiresAuth: true },
  { path: '/scheduled-tasks', component: ScheduledTasksPage, requiresAuth: true },
  { path: '/tasks-notifications', component: TaskNotificationsPage, requiresAuth: true },
  { path: '/incoming-tasks-settings', component: React.lazy(() => import('@/pages/IncomingTasksSettingsPage')), requiresAuth: true },

  // SAIO Dashboard
  { path: '/saio-dashboard', component: SaioDashboardPage, requiresAuth: true },

  // SiteAudit Dashboard
  { path: '/siteaudit/dashboard', component: React.lazy(() => import('@/pages/siteaudit/SiteAuditDashboardPage')), requiresAuth: true },

  // SAIO Routes
  ...saioRoutes.map(route => ({
    path: route.path,
    component: route.element,
    requiresAuth: true
  })),

  // Meta Generator
  { path: '/meta-generator', component: MetaGeneratorPage, requiresAuth: true },

  // SEO Routes
  ...seoRoutes.map(route => ({
    path: route.path,
    component: route.component,
    requiresAuth: true
  })),

  // Email Marketing Report
  { path: '/email-marketing-report', component: EmailMarketingReportPage, requiresAuth: true },

  // Product Tours Admin
  { path: '/product-tours-admin', component: ProductToursAdmin, requiresAuth: true },
  { path: '/product-tours-admin/create', component: ProductTourBuilder, requiresAuth: true },
  { path: '/product-tours-admin/builder/:tourId', component: ProductTourBuilder, requiresAuth: true },
  { path: '/product-tours-admin/target-elements', component: ProductTourTargetElements, requiresAuth: true },

  // HubSpot API Test
  { path: '/hubspot-test', component: HubSpotTestPage, requiresAuth: true },

  // E-commerce SEO
  { path: '/ecommerce-seo', component: EcommerceSeoPage, requiresAuth: true },

  // Keydates
  { path: '/keydates', component: KeydatesPage, requiresAuth: true },
  { path: '/keydates-prompts', component: KeydatesPromptsPage, requiresAuth: true },

  // Quizzes
  { path: '/quizzes', component: QuizListPage, requiresAuth: true },
  { path: '/quizzes/new', component: QuizBuilderPage, requiresAuth: true },
  { path: '/quizzes/:quizId/edit', component: QuizBuilderPage, requiresAuth: true },
  { path: '/quizzes/:quizId/preview', component: QuizPreviewPage, requiresAuth: true },
  { path: '/quiz-performance', component: QuizPerformancePage, requiresAuth: true },
  { path: '/quiz-templates', component: QuizTemplatesPage, requiresAuth: true },
  { path: '/quiz-styles', component: QuizStylesPage, requiresAuth: true },
  { path: '/quiz-prompts', component: QuizPromptsPage, requiresAuth: true },
  { path: '/quiz-sources', component: QuizSourcesPage, requiresAuth: true },

  // Press Releases
  { path: '/press-releases', component: PressReleasesPage, requiresAuth: true },
  { path: '/journalists', component: JournalistsPage, requiresAuth: true },

  // Link Building
  { path: '/link-building/analytics', component: LinkBuildingAnalyticsPage, requiresAuth: true },
  { path: '/link-building/partners', component: LinkBuildingPartnersPage, requiresAuth: true },
  { path: '/link-building/domains', component: LinkBuildingDomainsPage, requiresAuth: true },
  { path: '/link-building/exchanges', component: LinkBuildingExchangesPage, requiresAuth: true },
  { path: '/link-building/exchanges/new', component: LinkBuildingExchangeCreatePage, requiresAuth: true },
  { path: '/link-building/exchanges/:id', component: LinkBuildingExchangeDetailPage, requiresAuth: true },
  { path: '/link-building/exchanges/:id/edit', component: LinkBuildingExchangeDetailPage, requiresAuth: true },

  // Polls
  { path: '/polls', component: PollsPage, requiresAuth: true },
  { path: '/polls/new', component: PollBuilderPage, requiresAuth: true },
  { path: '/polls/:pollId/edit', component: PollBuilderPage, requiresAuth: true },
  { path: '/polls/:pollId/preview', component: PollPreviewPage, requiresAuth: true },
  { path: '/polls/:pollId/results', component: PollResultsPage, requiresAuth: true },
  { path: '/poll-sources', component: PollSourcesPage, requiresAuth: true },
  { path: '/poll-performance', component: PollPerformancePage, requiresAuth: true },
  { path: '/poll-styles', component: PollStylesPage, requiresAuth: true },
  { path: '/poll-templates', component: PollTemplatesPage, requiresAuth: true },

  // Lead Magnets
  { path: '/lead-magnets', component: LeadMagnetsPage, requiresAuth: true },

  // Mini Games
  { path: '/mini-games', component: MiniGameListPage, requiresAuth: true },
  { path: '/mini-games/:id/edit', component: React.lazy(() => import('@/pages/MiniGameEditPage')), requiresAuth: true },
  { path: '/mini-games/builder', component: MiniGameBuilderPage, requiresAuth: true },
  { path: '/mini-games/builder/:gameId', component: MiniGameBuilderPage, requiresAuth: true },
  { path: '/mini-games/play/:gameId', component: MiniGamePlayerPage, requiresAuth: true },
  { path: '/mini-games/:gameId/preview', component: MiniGamePlayerPage, requiresAuth: true },
  { path: '/mini-game-types', component: MiniGameTypesPage, requiresAuth: true },
  { path: '/mini-game-templates', component: MiniGameTemplatesPage, requiresAuth: true },
  { path: '/mini-game-templates/new', component: React.lazy(() => import('@/pages/MiniGameTemplateBuilderPage')), requiresAuth: true },
  { path: '/mini-game-templates/:templateId', component: React.lazy(() => import('@/pages/MiniGameTemplateBuilderPage')), requiresAuth: true },
  { path: '/mini-game-sources', component: MiniGameSourcesPage, requiresAuth: true },
  { path: '/mini-game-prompts', component: MiniGamePromptsPage, requiresAuth: true },
  { path: '/mini-game-performance', component: MiniGamePerformancePage, requiresAuth: true },

  // Documentation Hub
  { path: '/documentation-hub/dashboard', component: DocumentationHubDashboardPage, requiresAuth: true },
  { path: '/documentation-hub/events', component: DocumentationHubEventsPage, requiresAuth: true },
  { path: '/documentation-hub/documents', component: DocumentationHubDocumentsPage, requiresAuth: true },
  { path: '/documentation-hub/files/:slug', component: DocumentationHubFileDetailPage, requiresAuth: true },
  { path: '/documentation-hub/tips', component: DocumentationHubTipsPage, requiresAuth: true },
  { path: '/documentation-hub/tips/:slug', component: DocumentationHubTipDetailPage, requiresAuth: true },
  { path: '/documentation-hub/snippets', component: DocumentationHubSnippetsPage, requiresAuth: true },
  { path: '/documentation-hub/snippets/:slug', component: DocumentationHubSnippetDetailPage, requiresAuth: true },
  { path: '/documentation-hub/project-moms', component: ProjectMomsPage, requiresAuth: true },
  { path: '/incoming-transcripts', component: IncomingTranscriptsPage, requiresAuth: true },
  { path: '/incoming-transcripts/webhook-guide', component: IncomingTranscriptsWebhookGuide, requiresAuth: true },

  // Support Portal
  { path: '/support-portal', component: SupportPortalPage, requiresAuth: true },
  { path: '/support-portal/category/:categorySlug', component: SupportPortalPage, requiresAuth: true },
  { path: '/support-portal/article/:articleSlug', component: SupportPortalPage, requiresAuth: true },
  { path: '/admin/support-portal/articles', component: React.lazy(() => import('@/pages/admin/SupportPortalArticlesPage')), requiresAuth: true },
  { path: '/admin/support-portal/articles/new', component: SupportPortalEditorPage, requiresAuth: true },
  { path: '/admin/support-portal/articles/:id/edit', component: SupportPortalEditorPage, requiresAuth: true },
  { path: '/admin/support-portal/categories', component: SupportPortalCategoriesPage, requiresAuth: true },
  { path: '/admin/support-portal/videos', component: React.lazy(() => import('@/pages/admin/SupportPortalVideosPage')), requiresAuth: true },

  // Pricing Admin
  { path: '/admin/pricing-modules', component: React.lazy(() => import('@/pages/admin/PricingModulesPage')), requiresAuth: true },
  { path: '/admin/pricing-credit-tiers', component: React.lazy(() => import('@/pages/admin/PricingCreditTiersPage')), requiresAuth: true },

  // Scale at Speed: 2Y3X
  { path: '/scale-2y3x/dashboard', component: Scale2Y3XDashboard, requiresAuth: true },
  { path: '/scale-2y3x/growth-lab-team', component: GrowthLabTeamPage, requiresAuth: true },
  { path: '/scale-2y3x/sprints', component: Scale2Y3XSprintsPage, requiresAuth: true },
  { path: '/scale-2y3x/goals', component: Scale2Y3XGoalsPage, requiresAuth: true },
  { path: '/scale-2y3x/ideas', component: Scale2Y3XIdeasPage, requiresAuth: true },
  { path: '/scale-2y3x/current-quarter-tasks', component: Scale2Y3XCurrentQuarterTasksPage, requiresAuth: true },
  { path: '/scale-2y3x/tasks', component: Scale2Y3XTasksPage, requiresAuth: true },
  { path: '/scale-2y3x/quarterly-tasks', component: Scale2Y3XQuarterlyTasksPage, requiresAuth: true },
  { path: '/scale-2y3x/2y3x-builder', component: Strategy2Y3XBuilderPage, requiresAuth: true },
  { path: '/scale-2y3x/about', component: About2Y3XPage, requiresAuth: true },
  { path: '/scale-2y3x/phases', component: Scale2Y3XPhasesPage, requiresAuth: true },
  { path: '/scale-2y3x/performance-insights', component: PerformanceInsightsPage, requiresAuth: true },

  // Report Shares
  { path: '/report-shares', component: ReportSharesPage, requiresAuth: true },
  { path: '/report-shares-settings', component: ReportSharesSettingsPage, requiresAuth: true },

  // CMS Settings
  { path: '/settings/cms-settings', component: React.lazy(() => import('@/pages/CMSSettingsPage')), requiresAuth: true },
  { path: '/settings/cms-embed-guide', component: React.lazy(() => import('@/pages/CMSEmbedGuide')), requiresAuth: true },

  // Not Found
  { path: '/not-found', component: NotFoundPage, requiresAuth: true },
];
